import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { resolveImage } from "@/lib/image-map";
import { toast } from "sonner";
import { X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/shopper-profile")({
  head: () => ({
    meta: [
      { title: "Your profile — Pluggz" },
      { name: "description", content: "Manage your shopper profile, followed creators and preferences on Pluggz." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ShopperProfilePage,
});

const ALL_CATEGORIES = [
  "Womens Fashion",
  "Beauty & Skincare",
  "Mother & Baby",
  "Home & Decor",
  "Fitness & Wellness",
  "Shoes & Accessories",
  "Cooking & Grocery",
  "Finance & Lifestyle",
  "Travel",
  "GRWM",
] as const;
type Category = (typeof ALL_CATEGORIES)[number];

type Profile = {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  followed_creators: string[];
  preferred_categories: string[];
};

function ShopperProfilePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id;

  const profile = useQuery({
    queryKey: ["shopper-profile", userId],
    enabled: !!userId,
    queryFn: async (): Promise<Profile> => {
      const { data } = await supabase
        .from("shopper_profiles")
        .select("*")
        .eq("user_id", userId!)
        .maybeSingle();
      if (data) return data as Profile;
      const blank: Profile = {
        user_id: userId!,
        display_name: user?.email?.split("@")[0] ?? null,
        avatar_url: null,
        followed_creators: [],
        preferred_categories: [],
      };
      await supabase.from("shopper_profiles").insert(blank);
      return blank;
    },
  });

  const followed = useQuery({
    queryKey: ["shopper-followed", profile.data?.followed_creators ?? []],
    enabled: !!profile.data && (profile.data.followed_creators?.length ?? 0) > 0,
    queryFn: async () => {
      const ids = profile.data!.followed_creators;
      const { data } = await supabase
        .from("creators")
        .select("id, handle, name, avatar_url")
        .in("id", ids);
      return data ?? [];
    },
  });

  const [displayName, setDisplayName] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile.data) {
      setDisplayName(profile.data.display_name ?? "");
      setCategories((profile.data.preferred_categories ?? []) as Category[]);
    }
  }, [profile.data]);

  async function save() {
    if (!userId) return;
    setSaving(true);
    const { error } = await supabase
      .from("shopper_profiles")
      .update({
        display_name: displayName.trim() || null,
        preferred_categories: categories,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Profile saved");
    qc.invalidateQueries({ queryKey: ["shopper-profile", userId] });
    qc.invalidateQueries({ queryKey: ["for-you-products"] });
    qc.invalidateQueries({ queryKey: ["followed-creator-products"] });
  }

  async function unfollow(creatorId: string) {
    if (!userId || !profile.data) return;
    const next = profile.data.followed_creators.filter((id) => id !== creatorId);
    const { error } = await supabase
      .from("shopper_profiles")
      .update({ followed_creators: next, updated_at: new Date().toISOString() })
      .eq("user_id", userId);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["shopper-profile", userId] });
  }

  function toggleCategory(c: Category) {
    setCategories((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  }

  if (profile.isLoading) {
    return <div className="mx-auto max-w-2xl px-6 py-20 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-16 space-y-12">
      <div>
        <p className="editorial-eyebrow">Your profile</p>
        <h1 className="mt-2 font-serif text-4xl">Make Pluggz yours</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A few preferences help us surface the storefronts and products you'll love.
        </p>
      </div>

      <section className="space-y-4">
        <h2 className="font-serif text-xl">Display name</h2>
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={60}
          placeholder="Your name"
          className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:border-foreground"
        />
      </section>

      <section className="space-y-4">
        <h2 className="font-serif text-xl">Avatar</h2>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 overflow-hidden rounded-full border border-border bg-muted">
            {profile.data?.avatar_url ? (
              <img src={profile.data.avatar_url} alt={`${profile.data.display_name || "Your"} profile photo`} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-muted-foreground">
                {(displayName || "?").slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled
            className="border border-border px-4 py-2 text-xs uppercase tracking-widest text-muted-foreground opacity-60"
            title="Avatar upload coming soon"
          >
            Upload avatar (soon)
          </button>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-serif text-xl">Preferred categories</h2>
        <p className="text-sm text-muted-foreground">We'll feature these on your homepage.</p>
        <div className="flex flex-wrap gap-2">
          {ALL_CATEGORIES.map((c) => {
            const on = categories.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleCategory(c)}
                className={`border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${on ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-serif text-xl">Creators you follow</h2>
        {(followed.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You don't follow anyone yet. <Link to="/creators" className="underline">Browse creators</Link>.
          </p>
        ) : (
          <ul className="divide-y divide-border border border-border bg-card">
            {followed.data!.map((c: any) => (
              <li key={c.id} className="flex items-center gap-3 px-4 py-3">
                <img src={resolveImage(c.avatar_url)} alt={c.name} className="h-10 w-10 rounded-full object-cover" />
                <div className="min-w-0 flex-1">
                  <Link to="/creator/$handle" params={{ handle: c.handle }} className="text-sm font-medium hover:underline">
                    {c.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">@{c.handle}</p>
                </div>
                <button
                  type="button"
                  onClick={() => unfollow(c.id)}
                  className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground"
                >
                  <X className="h-3 w-3" /> Unfollow
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-6">
        <button
          onClick={save}
          disabled={saving}
          className="bg-foreground px-6 py-3 text-xs uppercase tracking-widest text-background disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <Link
          to="/privacy-settings"
          className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground link-underline"
        >
          Privacy & delete account →
        </Link>
      </div>
    </div>
  );
}
